<script lang="ts" setup>
import { faBars, faSpinnerThird } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-light-svg-icons";
import type { Account } from "~/models/User";
import type { Company } from "~/services/CoreSignalService";

interface AppPageBreadCrumb {
    label: string | number;
    to?: string;
}

interface AppPageProps {
    title?: string;
    loading?: any;
    error?: boolean;
    class?: any;
    collapsed?: boolean;
    breadcrumbs?: AppPageBreadCrumb[];
    showTitle?: boolean;
}

const route = useRoute();

const { $intercom } = useNuxtApp();

withDefaults(defineProps<AppPageProps>(), {
    breadcrumbs: () => [],
    title: undefined,
    collapsed: false,
    loading: undefined,
    error: undefined,
    showTitle: undefined,
});

const { user: auth, refreshIdentity } = useAuth();
const { hasRole, hasAnyRoles } = useUser();
const api = useApi();
const settingsOpen = ref<boolean>(false);
const isMainNavOpen = ref<boolean>(false);
const isSwitchAccountModalOpen = ref<boolean>(false);
const isCreateAccountModalOpen = ref<boolean>(false);
const accounts = ref<Account[]>([]);
const submitting = ref<boolean>(false);
const selected = ref<Company | null>(null);

const user = reactive({
    email: auth.value?.email,
    account_id: auth.value?.account.id,
    account: "",
    role: auth.value?.role || "user",
    is_super_admin: auth.value?.is_super_admin,
});

useHead({
    titleTemplate: (titleChunk) => {
        return titleChunk ? `${titleChunk} - HireData` : "HireData";
    },
});

function showIntercom() {
    if (
        parseInt(
            document.querySelector(".intercom-app div[style]")?.style.opacity,
        ) > 0
    ) {
        $intercom.hide();
    } else {
        $intercom.show();
    }
}

const switchAccountModal = () =>
    (isSwitchAccountModalOpen.value = !isSwitchAccountModalOpen.value);

const createAccountModal = () => {
    isCreateAccountModalOpen.value = !isCreateAccountModalOpen.value;
};

const getAccountList = async () => {
    const { data } = await api.account.list();
    accounts.value = data;
};

async function updateAccount() {
    submitting.value = true;
    await api.auth.account.swap(user.account_id, {
        role: user.role,
    });
    if (route.params.id) {
        navigateTo("/");
    }
    await refreshIdentity();
    refreshNuxtData();
    switchAccountModal();
    submitting.value = false;
}

const switchTheAccount = async () => {
    submitting.value = true;
    await api.auth.account.swap(user.account_id);
    await refreshIdentity();
    createAccountModal();
    useToast().add({
        title: "Account switched!",
    });
    await refreshNuxtData();
    submitting.value = false;
};

const { t } = useI18n();

const roles = computed(() => {
    const roles = ["user"];

    if (auth.value?.is_super_admin || auth.value?.roles?.includes("owner")) {
        roles.push("admin", "owner");
    } else if (auth.value?.roles?.includes("admin")) {
        roles.push("admin");
    }

    return roles.map((role) => ({
        label: t(`labels.${role}`),
        value: role,
    }));
});

defineShortcuts({
    H: {
        usingInput: false,
        handler() {
            refreshIdentity();
            user.account_id = auth.value?.account.id;
            user.role = auth.value?.role;
            switchAccountModal();
            getAccountList();
        },
        whenever: [() => hasRole("super_admin")],
    },
});

function onError(error: Error) {
    console.error(error);
}
</script>

<template>
    <AppMainNavigation />
    <Title>{{ title ?? route?.meta?.title }}</Title>
    <div
        :class="$props.class"
        class="flex-1 flex flex-col bg-hd-neutral-50 [&>div:nth-child(2)]:bg-white md:[&>div:nth-child(2)]:rounded-tl-xl"
    >
        <div class="h-20 flex-shrink-0 w-full grid grid-cols-3 px-4">
            <div class="flex flex-1 items-center justify-start col-span-2">
                <UButton
                    class="md:hidden mr-2"
                    color="white"
                    size="sm"
                    square
                    variant="ghost"
                    @click="isMainNavOpen = true"
                >
                    <FontAwesomeIcon
                        :icon="faBars"
                        class="opacity-50"
                        size="lg"
                    />
                </UButton>
                <slot name="pre-title" />
                <div
                    v-if="showTitle"
                    class="flex flex-col flex-1 max-w-full gap-3"
                >
                    <div class="text-sm">
                        <h1 class="text-lg font-medium truncate font-display">
                            {{ title ?? route.meta.title }}
                        </h1>
                        <UIBreadCrumbs :breadcrumbs="breadcrumbs" />
                    </div>
                </div>
                <slot name="post-title" />
            </div>
            <div class="flex justify-end items-center gap-4">
                <slot name="actions" />
                <UButton
                    color="white"
                    size="md"
                    square
                    variant="ghost"
                    @click="showIntercom"
                >
                    <FontAwesomeIcon
                        :icon="faQuestionCircle"
                        class="opacity-50"
                        size="xl"
                    />
                </UButton>
                <AppCreateAutomation />
            </div>
        </div>
        <template v-if="loading === undefined || loading !== true">
            <template v-if="error">
                <slot name="error" />
            </template>
            <NuxtErrorBoundary v-else @error="onError">
                <template #error="{ error }">
                    <div class="w-full h-full flex items-center justify-center">
                        <UContainer
                            :ui="{ padding: 'px-6 py-32 sm:py-40 lg:px-8' }"
                            class="text-center"
                        >
                            <p
                                class="text-base font-semibold leading-8 text-primary"
                            >
                                500
                            </p>
                            <h1
                                class="mt-4 text-3xl font-bold tracking-tight text-hd-primary-original sm:text-5xl"
                            >
                                {{ error }}
                            </h1>
                        </UContainer>
                    </div>
                </template>
                <slot />
            </NuxtErrorBoundary>
        </template>
        <div v-else class="w-full flex-1 flex items-center justify-center">
            <FontAwesomeIcon
                :icon="faSpinnerThird"
                class="text-primary-500"
                size="2x"
                spin
            />
        </div>
    </div>
    <AppSettings v-model:open="settingsOpen" />
    <USlideover
        v-model="isMainNavOpen"
        :ui="{ background: 'bg-hd-neutral-50' }"
        side="left"
    >
        <AppMainNavigation />
    </USlideover>
    <UIModalSwitchAccount
        v-if="hasRole('super_admin')"
        v-model:open="isSwitchAccountModalOpen"
        v-model:user="user"
        :accounts="accounts"
        :create-account-modal="createAccountModal"
        :current-account-id="auth?.account.id"
        :current-role="auth?.role"
        :roles="roles"
        :switch-account-modal="switchAccountModal"
        :update-account="updateAccount"
    />
    <UiModalPartnerTenant
        v-else-if="
            (auth.account.is_partner || auth.current_partner_tenant) &&
            hasAnyRoles(['partner_manager', 'partner_user'])
        "
    />
    <UIModalCreateAccount
        v-model:open="isCreateAccountModalOpen"
        :create-account-modal="createAccountModal"
        :switch-account-modal="switchAccountModal"
        :switch-the-account="switchTheAccount"
    />
</template>
